import { format } from 'date-fns'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import type { Task } from '@percent/admin-dashboard/api/actions/grantProgram/grantProgram.types'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import type { LocaleKey } from '@percent/admin-dashboard/i18n/config'
import {
  AcknowledgeModal,
  AssigneePicker,
  Badge,
  type BadgeVariant,
  Button,
  ConfirmationModal,
  Flex,
  Icon,
  Loader,
  Modal,
  Typography
} from '@percent/lemonade'
import { useMutation, useQuery } from '@percent/utility'
import styles from './CandidateTasks.module.scss'
import type { CandidateTasksProps } from './CandidateTasks.types'
import { UpdateTaskStatusModal } from './updateTaskStatusModal/UpdateTaskStatusModal'

const getTaskStatus = (
  task: Task
): {
  status: 'todo' | 'overdue' | 'completed'
  variant: BadgeVariant
  label: LocaleKey
} => {
  const isCompleted = task.status === 'done'

  if (isCompleted)
    return {
      status: 'completed',
      variant: 'positive',
      label: 'grants.programDetails.candidates.tasks.completed'
    }

  const now = Date.now()
  const dueTime = task.dueByDate ? new Date(task.dueByDate).getTime() : 0
  const isOverdue = !Number.isNaN(dueTime) && dueTime < now

  if (isOverdue)
    return {
      status: 'overdue',
      variant: 'critical',
      label: 'grants.programDetails.candidates.tasks.overdue'
    }

  return {
    status: 'todo',
    variant: 'default',
    label: 'grants.programDetails.candidates.tasks.todo'
  }
}

export const sortTasksByPriority = (tasks: Task[]): Task[] => {
  return tasks.sort((a, b) => {
    const aStatus = getTaskStatus(a).status
    const bStatus = getTaskStatus(b).status

    const statusPriority = { overdue: 0, todo: 1, completed: 2 }

    if (statusPriority[aStatus] !== statusPriority[bStatus]) {
      return statusPriority[aStatus] - statusPriority[bStatus]
    }

    const aTime = a.dueByDate ? new Date(a.dueByDate).getTime() : 0
    const bTime = b.dueByDate ? new Date(b.dueByDate).getTime() : 0

    return aTime - bTime
  })
}

export function CandidateTasks({ candidateId }: Readonly<CandidateTasksProps>) {
  const [selectedTask, setSelectedTask] = useState<Task | null>(null)
  const [searchValues, setSearchValues] = useState<Record<string, string>>({})
  const [modalState, setModalState] = useState<'confirmation' | 'success' | 'error' | null>(null)
  const [pendingAssignment, setPendingAssignment] = useState<{
    taskId: string
    assignee: { id: string; email: string }
  } | null>(null)

  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const { grantProgramService } = useServices()

  const {
    data: tasks,
    isPending,
    refetch: refetchTasks
  } = useQuery(['/candidate', candidateId, 'tasks'], grantProgramService.getCandidateTasks, {
    candidateId
  })

  const { data: assignees, isPending: isAssigneesPending } = useQuery(
    ['/program', id, 'task-assignees'],
    grantProgramService.getTaskAssignees,
    {
      programId: id ?? ''
    }
  )

  const [{ isPending: isUpdating }, { apiFunc: updateAssignee }] = useMutation(
    grantProgramService.updateTaskAssignee,
    () => {
      refetchTasks()
      setSearchValues({})
      setModalState('success')
    },
    () => {
      setModalState('error')
    }
  )

  const handleAssigneeChange = (taskId: string, assignee: { id: string; email: string }) => {
    setPendingAssignment({ taskId, assignee })
    setModalState('confirmation')
  }

  const handleConfirmAssigneeChange = () => {
    if (!id || !pendingAssignment) return

    updateAssignee({
      programId: id,
      payload: {
        taskId: pendingAssignment.taskId,
        assignedTo: pendingAssignment.assignee.id
      }
    })
  }

  const handleCloseModal = () => {
    setPendingAssignment(null)
    setModalState(null)
  }

  const handleSearchChange = (taskId: string, value: string) => {
    setSearchValues(prev => ({ ...prev, [taskId]: value }))
  }

  if (isPending || isAssigneesPending) return <Loader />

  const successModal = modalState === 'success' && (
    <AcknowledgeModal
      result="positive"
      title={t('grants.programDetails.candidates.tasks.changeAssignee.success.title')}
      description={t('grants.programDetails.candidates.tasks.changeAssignee.success.text', {
        email: pendingAssignment?.assignee.email ?? ''
      })}
      buttonText={t('button.close')}
      handleClose={handleCloseModal}
      viewTestId="change-assignee-success-modal"
      buttonTestId="close-change-assignee-success-modal"
    />
  )

  const errorModal = modalState === 'error' && (
    <AcknowledgeModal
      result="negative"
      title={t('grants.programDetails.candidates.tasks.changeAssignee.error.title')}
      description={t('grants.programDetails.candidates.tasks.changeAssignee.error.description')}
      buttonText={t('button.close')}
      handleClose={handleCloseModal}
      viewTestId="change-assignee-error-modal"
      buttonTestId="close-change-assignee-error-modal"
    />
  )

  const confirmationModal = modalState === 'confirmation' && (
    <ConfirmationModal
      title={t('grants.programDetails.candidates.tasks.changeAssignee.title')}
      primaryButtonText={t('grants.programDetails.candidates.tasks.changeAssignee.confirm')}
      secondaryButtonText={t('grants.programDetails.candidates.tasks.changeAssignee.cancel')}
      handleSubmit={handleConfirmAssigneeChange}
      handleClose={handleCloseModal}
      loading={isUpdating}
      primaryBtnTestId="confirm-change-assignee-button"
      secondaryBtnTestId="cancel-change-assignee-button"
    >
      {t('grants.programDetails.candidates.tasks.changeAssignee.description', {
        email: pendingAssignment?.assignee.email ?? ''
      })}
    </ConfirmationModal>
  )

  return (
    <>
      <Flex direction="column" gap={16} mt={16} data-testid="tasks-list">
        {sortTasksByPriority(tasks ?? []).map(task => {
          const { status, variant, label } = getTaskStatus(task)

          const taskName = task.audience ? `${task.name} (${task.audience})` : task.name

          const filteredAssignees = assignees?.filter(assignee =>
            assignee.email.toLowerCase().includes((searchValues[task.id] || '').toLowerCase())
          )

          return (
            <Flex key={task.id} data-testid={`task-${task.id}`} className={styles.taskContainer} data-status={status}>
              <Flex direction="row" justify="space-between">
                <Badge variant={variant} color="gray">
                  {taskName}
                </Badge>

                {task.dueByDate && (
                  <Flex align="center" className={styles.deadline}>
                    <Icon name="clock" size="s" />
                    <Typography variant="small" data-testid="task-due-date">
                      {format(new Date(task.dueByDate), 'd MMM yyyy')}
                    </Typography>
                  </Flex>
                )}
              </Flex>

              <Flex className={styles.taskWrapper}>
                <AssigneePicker
                  assigneeList={filteredAssignees ?? []}
                  onAssigneeChange={assignee => handleAssigneeChange(task.id, assignee)}
                  assignee={{
                    email: task.assignee?.email ?? '',
                    id: task.assignee?.accountId ?? ''
                  }}
                  status={status}
                  statusLabel={t(label)}
                  search={{
                    value: searchValues[task.id] || '',
                    onChange: value => handleSearchChange(task.id, value),
                    placeholder: 'Search assignee'
                  }}
                  tooltipContent={
                    task.assignee?.email
                      ? t('grants.programDetails.candidates.tasks.assignee', {
                          email: task.assignee?.email
                        })
                      : t('grants.programDetails.candidates.tasks.unassigned')
                  }
                />

                <Button variant="secondary" size="small" onClick={() => setSelectedTask(task)}>
                  {t('grants.programDetails.candidates.tasks.updateStatus')}
                </Button>
              </Flex>
            </Flex>
          )
        })}
      </Flex>

      {selectedTask && (
        <UpdateTaskStatusModal task={selectedTask} refresh={refetchTasks} onClose={() => setSelectedTask(null)} />
      )}

      <Modal open={!!modalState} onClose={handleCloseModal} showModalOverflow={false}>
        {successModal || errorModal || confirmationModal}
      </Modal>
    </>
  )
}
